import React, { useState } from "react"

import { NotificationManager } from "react-notifications"
import { OperationContext } from "urql"

import { DescriptionItem } from "~tailwindui/Basics"

import { adminClient as client } from "src/urql-client"

import { gql, useMutation } from "hooks/urql"

import ModalWithProvidedBody from "components/ModalWithProvidedBody"

type Degree = {
  id: string
  college: string
  year: number
  country: string
  degree: string
  degreeType: string
  level: string
  levelOther: string
  inUsa: boolean
  diplomaMill: boolean
  tutor: {
    id: string
    fullName: string
    showPath: string
  }
  transcript: {
    filename: string
    downloadUrl: string
  }
}

type DegreeInfoModalProps = {
  degree: Degree
  refetchDegrees: (opts: Partial<OperationContext>) => void
}

const DegreeInfoModal: React.FC<DegreeInfoModalProps> = ({
  degree,
  refetchDegrees,
}) => {
  const [errors, setErrors] = useState<undefined | string>()
  const [, runMutation]: any[] = useMutation(
    updateDegreeApprovalStateMutation,
    client
  )

  const handleSubmit = (approved, closeModal) => {
    runMutation({ id: degree.id, approved })
      .then(result => {
        const { failures } = result.data.updateDegreeApprovalState
        if (failures.length > 0) {
          setErrors(failures[0].message)
        } else {
          refetchDegrees({ requestPolicy: "network-only" })
          closeModal()
          NotificationManager.info(
            `Degree has been ${approved ? "approved" : "rejected"}`
          )
        }
      })
      .catch(() => setErrors("Something went wrong"))
  }

  return (
    <ModalWithProvidedBody
      modalTitle="Degree Approval"
      buttonText="View"
      buttonClassName="btn btn-info"
    >
      {({ closeModal }) => (
        <>
          <div className="modal-body">
            <DescriptionItem
              label="Tutor"
              value={
                <a href={degree.tutor.showPath}>{degree.tutor.fullName}</a>
              }
            />
            <DescriptionItem label="Country" value={degree.country} />
            <DescriptionItem label="Year" value={degree.year} />
            <DescriptionItem label="Field of Study" value={degree.degree} />
            <DescriptionItem label="Degree Type" value={degree.degreeType} />
            <DescriptionItem
              label="Level"
              value={
                degree.level === "other" ? degree.levelOther : degree.level
              }
            />
            <DescriptionItem label="College" value={degree.college} />
            <DescriptionItem label="Diploma Mill" value={degree.diplomaMill} />
            <DescriptionItem
              label="Transcript File"
              value={
                degree.transcript.downloadUrl ? (
                  <a
                    className="btn-link text-info pl-0"
                    href={degree.transcript.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {degree.transcript.filename}
                  </a>
                ) : (
                  "None"
                )
              }
            />
          </div>

          <div className="modal-footer flex-col">
            {errors && <div className="alert alert-danger">{errors}</div>}

            <div className="w-100 flex justify-end space-x-2">
              <button
                onClick={() => handleSubmit(false, closeModal)}
                className="btn btn-danger"
              >
                Reject
              </button>
              <button
                onClick={() => handleSubmit(true, closeModal)}
                className="btn btn-success"
              >
                Approve
              </button>
            </div>
          </div>
        </>
      )}
    </ModalWithProvidedBody>
  )
}

const updateDegreeApprovalStateMutation = gql`
  mutation ($id: ID!, $approved: Boolean!) {
    updateDegreeApprovalState(id: $id, approved: $approved) {
      failures {
        message
      }
    }
  }
`

export default DegreeInfoModal
